import { getGameRuntimeConfig } from "../api/config";
import { AuthClient, type LoginResult } from "../api/auth";
import { configureUnauthorizedRecovery } from "../api/http";
import {
  FORUM_AUTH_SOURCE,
  FORUM_AUTH_SOURCE_QUERY_KEY,
  FORUM_AUTH_STATE_QUERY_KEY,
  GAME_PORTAL_ACTION_QUERY_KEY,
  GAME_PORTAL_JWT_QUERY_KEY,
  GAME_PORTAL_MODEL_QUERY_KEY,
  GAME_PORTAL_PACKAGE_QUERY_KEY,
  consumeGamePortalState,
  isNativeGameWebView,
  parseGamePortalCallbackLocation
} from "../forum-auth";
import { persistPreferredModelId } from "../api/models";
import { readSameOriginPortalSession } from "../embed/game-embed";
import { readMembership, resolveMembership, type MembershipState } from "../membership";
import { createId, sha256Digest } from "../utils/crypto";

export type AuthMode = "guest" | "anonymous" | "registered";
export type LlmStatus = "guest" | "ready" | "calling" | "fallback";

export interface SessionState {
  authMode: AuthMode;
  token?: string;
  refreshToken?: string;
  user?: unknown;
  isAnonymous?: boolean;
  installId?: string;
  fingerprintHash?: string;
  membership?: MembershipState;
  llmStatus: LlmStatus;
  lastError?: string;
}

export interface AnonymousFingerprint {
  packageId: number;
  installId: string;
  deviceFingerprint: string;
  fingerprintComponents: Record<string, unknown>;
}

export interface AnonymousSessionControllerOptions {
  getSession: () => SessionState;
  onSession: (session: SessionState) => void;
  onRecoveryError?: (error: unknown) => void;
  createFingerprint?: () => Promise<AnonymousFingerprint>;
  registerAnonymous?: (fingerprint: AnonymousFingerprint) => Promise<LoginResult>;
}

export interface AnonymousSessionController {
  ensure: () => Promise<string>;
  installUnauthorizedRecovery: () => () => void;
}

export interface GamePortalCallbackResult {
  token?: string;
  modelId?: number;
  packageId?: number;
  action?: "auth" | "model";
}

function readGamePortalCallback(): GamePortalCallbackResult {
  if (typeof window === "undefined") return {};

  try {
    const callback = parseGamePortalCallbackLocation(window.location.href);
    if (!callback) return {};
    const token = callback.params.get(GAME_PORTAL_JWT_QUERY_KEY)?.trim();
    const authSource = callback.params.get(FORUM_AUTH_SOURCE_QUERY_KEY)?.trim();
    const receivedState = callback.params.get(FORUM_AUTH_STATE_QUERY_KEY)?.trim() ?? "";
    const receivedAction = callback.params.get(GAME_PORTAL_ACTION_QUERY_KEY)?.trim();
    const packageId = Number(callback.params.get(GAME_PORTAL_PACKAGE_QUERY_KEY));
    const modelId = Number(callback.params.get(GAME_PORTAL_MODEL_QUERY_KEY));

    // 凭证与校验参数只用于启动接管；读取后立即清理，避免复制链接或后续跳转继续携带。
    window.history.replaceState(window.history.state, "", callback.cleanPath);

    // APK 门户回跳必须匹配一次性 state；不含门户校验字段的旧令牌注入走独立接管分支。
    const hasPortalRoundTrip = Boolean(receivedState || receivedAction || Number.isInteger(packageId));
    if (isNativeGameWebView() || hasPortalRoundTrip) {
      const isTrustedCallback = authSource === FORUM_AUTH_SOURCE
        && consumeGamePortalState(receivedState, receivedAction, packageId);
      if (!isTrustedCallback) return {};
    } else {
      return { token: token || undefined };
    }

    const action = receivedAction === "model" ? "model" : receivedAction === "auth" ? "auth" : undefined;
    const safePackageId = Number.isInteger(packageId) && packageId > 0 ? packageId : undefined;
    const safeModelId = action === "model" && Number.isInteger(modelId) && modelId > 0 ? modelId : undefined;
    if (safePackageId && safeModelId) persistPreferredModelId(safePackageId, safeModelId);
    return { token: token || undefined, modelId: safeModelId, packageId: safePackageId, action };
  } catch {
    // 非标准 WebView 地址解析失败时保留游客流程，不能阻塞游戏启动。
    return {};
  }
}

// 模块初始化时缓存官网回传，保证 React StrictMode 重复执行 hydration 时仍得到同一结果。
const sourceGamePortalCallback = readGamePortalCallback();

export function getGamePortalCallbackResult() {
  return sourceGamePortalCallback;
}

function readOrCreateInstallId() {
  const { packageId } = getGameRuntimeConfig();
  const key = `apk-games:${packageId}:install-id`;

  if (typeof window === "undefined") return createId();

  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;

    const next = createId();
    window.localStorage.setItem(key, next);
    return next;
  } catch {
    return createId();
  }
}

function readScreenSignal() {
  if (typeof window === "undefined") return "server";
  return `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
}

function readCanvasSignal(packageId: number) {
  if (typeof document === "undefined") return "server";

  try {
    const canvas = document.createElement("canvas");
    canvas.width = 180;
    canvas.height = 48;
    const context = canvas.getContext("2d");
    if (!context) return "none";

    context.textBaseline = "top";
    context.font = "16px serif";
    context.fillStyle = "#d6a13d";
    context.fillText(`${packageId}-fingerprint`, 4, 8);
    return canvas.toDataURL().slice(0, 160);
  } catch {
    return "blocked";
  }
}

export async function buildAnonymousFingerprint(): Promise<AnonymousFingerprint> {
  const { packageId } = getGameRuntimeConfig();
  const installId = readOrCreateInstallId();
  const components: Record<string, unknown> = {
    packageId,
    installId,
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: "deviceMemory" in navigator ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory : undefined,
    maxTouchPoints: navigator.maxTouchPoints,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: readScreenSignal(),
    viewport: typeof window === "undefined" ? "server" : `${window.innerWidth}x${window.innerHeight}`,
    canvas: readCanvasSignal(packageId)
  };

  try {
    const { default: FingerprintJS } = await import("@fingerprintjs/fingerprintjs");
    const agent = await FingerprintJS.load();
    const result = await agent.get();
    components.fingerprintjsVisitorId = result.visitorId;
    components.confidence = result.confidence;
  } catch {
    components.fingerprintjsVisitorId = "unavailable";
  }

  return {
    packageId,
    installId,
    deviceFingerprint: await sha256Digest(JSON.stringify(components)),
    fingerprintComponents: components
  };
}

export const buildDeviceFingerprint = buildAnonymousFingerprint;

export class SessionStore {
  static empty(): SessionState {
    return { authMode: "guest", llmStatus: "guest" };
  }

  static normalize(session?: Partial<SessionState>): SessionState {
    if (!session) return SessionStore.empty();
    // 匿名注册同样会创建后端用户；只要已有账号 token，就至少按免费会员补齐权益身份。
    const membership = session.token || session.user || session.membership
      ? resolveMembership(session.user, session.membership)
      : undefined;
    return {
      ...SessionStore.empty(),
      ...session,
      authMode: session.authMode ?? (session.token ? "registered" : "guest"),
      membership,
      llmStatus: session.llmStatus ?? (session.token ? "ready" : "guest")
    };
  }

  static hydrate(session?: Partial<SessionState>): SessionState {
    const normalized = SessionStore.normalize(session);
    const sameOriginPortal = readSameOriginPortalSession();
    const portalToken = sourceGamePortalCallback.token || sameOriginPortal.token;
    if (!portalToken) {
      // 官网同源 Web 运行环境以官网 token 为正式账号权威源；官网已退出时不得恢复旧正式账号。
      if (sameOriginPortal.available && normalized.authMode === "registered") return SessionStore.logout(normalized);
      return normalized;
    }

    // 官网同源会话或受控回传优先于设备上残留的匿名/其他账号，但保留设备指纹以供后续匿名降级。
    return SessionStore.withLogin(
      SessionStore.logout(normalized),
      portalToken,
      undefined,
      undefined,
      false,
      normalized.installId,
      normalized.fingerprintHash
    );
  }

  static withLogin(session: SessionState, token: string, refreshToken?: string, user?: unknown, isAnonymous = false, installId?: string, fingerprintHash?: string, membership?: MembershipState): SessionState {
    // 官网正式会话可能在匿名注册请求返回前通过 iframe 桥接完成；迟到的匿名结果不得覆盖正式账号。
    if (isAnonymous && session.authMode === "registered" && session.token) return session;
    // 匿名与邮箱账号只在后续绑定方式上有差异，模型权益都属于普通用户体系。
    const serverMembership = readMembership(user) ?? resolveMembership(undefined, membership);
    return {
      ...session,
      token,
      refreshToken,
      user,
      authMode: isAnonymous ? "anonymous" : "registered",
      isAnonymous,
      installId: installId ?? session.installId,
      fingerprintHash: fingerprintHash ?? session.fingerprintHash,
      membership: serverMembership,
      lastError: undefined,
      llmStatus: "ready"
    };
  }

  static withCalling(session: SessionState): SessionState {
    return {
      ...session,
      llmStatus: session.token ? "calling" : "guest"
    };
  }

  static withError(session: SessionState, error: string): SessionState {
    return {
      ...session,
      lastError: error,
      llmStatus: session.token ? "fallback" : "guest"
    };
  }

  static preserveRecoveredAnonymousSession(current: SessionState, next: SessionState): SessionState {
    if (
      current.authMode !== "anonymous"
      || next.authMode !== "anonymous"
      || !current.token
      || !next.token
      || current.token === next.token
    ) return next;

    // 异步叙事可能用旧快照回写状态；只接收其调用状态和错误，不允许覆盖刚恢复的新匿名凭证。
    return {
      ...next,
      token: current.token,
      refreshToken: current.refreshToken,
      user: current.user,
      isAnonymous: true,
      installId: current.installId ?? next.installId,
      fingerprintHash: current.fingerprintHash ?? next.fingerprintHash,
      membership: current.membership
    };
  }

  static logout(session?: SessionState): SessionState {
    return {
      authMode: "guest",
      llmStatus: "guest",
      installId: session?.installId,
      fingerprintHash: session?.fingerprintHash
    };
  }
}

export function createAnonymousSessionController(options: AnonymousSessionControllerOptions): AnonymousSessionController {
  let registration: Promise<string> | undefined;
  const createFingerprint = options.createFingerprint ?? buildAnonymousFingerprint;
  const registerAnonymous = options.registerAnonymous ?? ((fingerprint) => AuthClient.registerAnonymous(fingerprint));

  async function register() {
    if (registration) return registration;

    registration = (async () => {
      const fingerprint = await createFingerprint();
      const result = await registerAnonymous(fingerprint);
      // 重注册只替换账号会话，installId 与设备指纹继续绑定同一设备，游戏存档不参与变更。
      const session = SessionStore.withLogin(
        options.getSession(),
        result.token,
        result.refreshToken,
        result.user,
        true,
        fingerprint.installId,
        fingerprint.deviceFingerprint,
        result.membership
      );
      options.onSession(session);
      return result.token;
    })();

    try {
      return await registration;
    } finally {
      registration = undefined;
    }
  }

  async function ensure() {
    const token = options.getSession().token;
    return token ?? register();
  }

  function installUnauthorizedRecovery() {
    return configureUnauthorizedRecovery(
      async ({ token: rejectedToken }) => {
        const current = options.getSession();
        // 正式账号 401 必须交给登录/刷新流程，不能悄悄切换成匿名账号。
        if (current.authMode !== "anonymous") return undefined;
        // 并发请求可能在旧请求返回 401 前已完成恢复，此时直接复用刚取得的新 token。
        if (current.token && current.token !== rejectedToken) return current.token;
        if (current.token !== rejectedToken) return undefined;

        try {
          return await register();
        } catch (error) {
          options.onRecoveryError?.(error);
          return undefined;
        }
      },
      (requestToken) => {
        const current = options.getSession();
        // 仅匿名态可替换长任务里的旧快照 token；正式账号切换和退出登录不做隐式接管。
        return current.authMode === "anonymous" && current.token && current.token !== requestToken
          ? current.token
          : undefined;
      }
    );
  }

  return { ensure, installUnauthorizedRecovery };
}
